import {
  LOCALES,
  LOCALE_NAMES,
  LOCALE_SHORT,
  LOCALE_TAGS,
  localePath,
  type Locale,
} from '../../shared/locales.js';
import { getStrings } from '../../shared/strings.js';
import { writeLanguagePreference } from '../lib/language.js';

/**
 * Language switcher.
 *
 * A plain `<details>` disclosure containing real links, not a scripted dropdown. That
 * choice buys a lot: it works before hydration, it works with JavaScript disabled, the
 * keyboard behaviour is the browser's own, and crawlers follow the links to every
 * translation.
 *
 * Each entry points at the same page in the other language, so switching language on the
 * FAQ keeps you on the FAQ. Choosing one also records the preference, which is what stops
 * a later visit to the bare domain from bouncing the reader back to their browser's
 * language.
 *
 * Inside a private room there is no locale in the URL and nothing to navigate to, so the
 * menu takes an `onSelect` handler and switches the language in place instead.
 */

interface LanguageMenuProps {
  locale: Locale;
  /** The current route without its locale prefix, for example `/faq`. */
  path?: string;
  /**
   * Override the destination per language. Article pages need it, because their URL
   * segment is translated too and the default "same path, other prefix" rule would point
   * at a page that does not exist.
   */
  hrefFor?: (locale: Locale) => string;
  /** Switch in place rather than navigate. Used by the private room. */
  onSelect?: (locale: Locale) => void;
}

export function LanguageMenu({ locale, path = '/', hrefFor, onSelect }: LanguageMenuProps) {
  const s = getStrings(locale);

  return (
    <details className="lang">
      <summary className="lang__button" aria-label={s.common.chooseLanguage}>
        <svg
          className="lang__icon"
          viewBox="0 0 20 20"
          width="16"
          height="16"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <circle cx="10" cy="10" r="7.5" />
          <path d="M2.5 10h15M10 2.5c2 2.4 3 4.9 3 7.5s-1 5.1-3 7.5c-2-2.4-3-4.9-3-7.5s1-5.1 3-7.5Z" />
        </svg>
        <span>{LOCALE_SHORT[locale]}</span>
      </summary>

      <ul className="lang__list">
        {LOCALES.map((option) => {
          const current = option === locale ? { 'aria-current': 'true' as const } : {};

          if (onSelect) {
            return (
              <li key={option}>
                <button
                  type="button"
                  className="lang__option"
                  lang={LOCALE_TAGS[option]}
                  onClick={() => {
                    writeLanguagePreference(option);
                    onSelect(option);
                  }}
                  {...current}
                >
                  {LOCALE_NAMES[option]}
                </button>
              </li>
            );
          }

          return (
            <li key={option}>
              <a
                className="lang__option"
                href={hrefFor ? hrefFor(option) : localePath(option, path)}
                lang={LOCALE_TAGS[option]}
                hrefLang={LOCALE_TAGS[option]}
                onClick={() => {
                  writeLanguagePreference(option);
                }}
                {...current}
              >
                {LOCALE_NAMES[option]}
              </a>
            </li>
          );
        })}
      </ul>
    </details>
  );
}
